import { CheckCircle2, XCircle } from "lucide-react"
import { ImportJobResult } from "../../types"

/**
 * Displays a summary of imported posts with statistics and details
 * Shows counts for successful, skipped, and failed imports
 */
export function PostImportSummary({ postsJob }: { postsJob: ImportJobResult }) {
  return (
    <div className="space-y-4">
      {/* Post import count summary */}
      <div className="space-y-2">
        <h3 className="text-base font-medium">Import Summary</h3>
        <div className="rounded-md border p-4 bg-muted/30">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-lg font-semibold">{postsJob.total}</div>
              <div className="text-xs text-muted-foreground">Total Posts</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-green-600">
                {postsJob.successful}
              </div>
              <div className="text-xs text-muted-foreground">Successful</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-amber-600">
                {postsJob.skipped + postsJob.errors}
              </div>
              <div className="text-xs text-muted-foreground">
                Skipped/Failed
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed breakdown section */}
      <DetailedBreakdown postsJob={postsJob} />

      <div className="pt-2">
        <p className="text-sm text-muted-foreground">
          All imported posts are saved as drafts. You can review and publish
          them from your dashboard.
        </p>
      </div>
    </div>
  )
}

/**
 * Shows a detailed breakdown of imported posts with specific reasons for failures
 */
function DetailedBreakdown({ postsJob }: { postsJob: ImportJobResult }) {
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium">Details</h3>

      {/* Successful posts summary */}
      {postsJob.successful > 0 && (
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <p className="text-sm text-foreground">
            {postsJob.successful} post
            {postsJob.successful !== 1 ? "s" : ""}{" "}
            {postsJob.successful === 1 ? "has" : "have"} been successfully
            imported
          </p>
        </div>
      )}

      {/* Skipped posts section */}
      {postsJob.skipped > 0 && <SkippedPostsSection postsJob={postsJob} />}

      {/* Failed posts section */}
      {postsJob.errors > 0 && <FailedPostsSection postsJob={postsJob} />}
    </div>
  )
}

/**
 * Shows information about skipped posts
 */
function SkippedPostsSection({ postsJob }: { postsJob: ImportJobResult }) {
  return (
    <div>
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex-shrink-0">
          <XCircle className="h-5 w-5 text-amber-500" />
        </div>
        <div className="space-y-2 w-full">
          <p className="text-sm text-foreground">
            {postsJob.skipped} post
            {postsJob.skipped !== 1 ? "s" : ""}{" "}
            {postsJob.skipped === 1 ? "was" : "were"} skipped
            <span className="text-muted-foreground">
              {" "}
              (already {postsJob.skipped === 1 ? "exists" : "exist"} - enable
              "Overwrite" to replace)
            </span>
          </p>
        </div>
      </div>

      {postsJob.skippedDetails && postsJob.skippedDetails.length > 0 && (
        <div className="mt-3 mb-4 w-full">
          <div className="max-h-40 overflow-y-auto pr-2 border border-muted rounded-sm bg-muted/10 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent w-full">
            <ul className="space-y-1 text-xs text-muted-foreground p-2 w-full">
              {postsJob.skippedDetails.map((skipped, i) => (
                <li key={i} className="flex flex-col">
                  <span>{skipped.title}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Shows information about failed posts
 */
function FailedPostsSection({ postsJob }: { postsJob: ImportJobResult }) {
  return (
    <div className="space-y-2">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 flex-shrink-0">
          <XCircle className="h-5 w-5 text-destructive" />
        </div>
        <div className="space-y-2 w-full">
          <p className="text-sm text-foreground">
            {postsJob.errors} post{postsJob.errors !== 1 ? "s" : ""}{" "}
            {postsJob.errors === 1 ? "has" : "have"} failed to import
          </p>
        </div>
      </div>

      {postsJob.errorDetails && postsJob.errorDetails.length > 0 && (
        <div className="mt-3 mb-4 w-full">
          <div className="max-h-40 overflow-y-auto pr-2 border border-muted rounded-sm bg-muted/10 scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent w-full">
            <ul className="space-y-1 text-xs text-muted-foreground p-2 w-full">
              {postsJob.errorDetails.map((error, i) => (
                <li key={i} className="flex flex-col mb-1">
                  <span className="font-medium">{error.title}</span>
                  {error.reason && (
                    <span className="text-destructive-foreground/70">
                      {error.reason}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        This may be due to formatting issues or unsupported content in the
        source file.
      </p>
    </div>
  )
}
